import { Image } from "@/components/common/Image";
import clsx, { ClassValue } from "clsx";

const MATERIAL_ICONS_CLASSNAME = "material-icons-outlined";
const MATERIAL_ICONS_FILLED_CLASSNAME = "material-icons";

export type IconProps = Readonly<{
  name: IconName;
  size?: string;
  className?: ClassValue;
  id?: string;
  onClick?: (e?: React.MouseEvent | React.KeyboardEvent) => void;
  "aria-label"?: string;
  fill?: boolean;
}>;

export type IconType = (props: Partial<IconProps>) => JSX.Element;

export type IconName =
  | "Add"
  | "AddPhoto"
  | "AccountCircle"
  | "Apps"
  | "ArrowUp"
  | "ArrowLeft"
  | "ArrowRight"
  | "Article"
  | "Bell"
  | "Calendar"
  | "Camera"
  | "Cancel"
  | "Carousel"
  | "Category"
  | "ChartBar"
  | "Chat"
  | "Check"
  | "CheckCircle"
  | "Checklist"
  | "ChevronRight"
  | "ChevronLeft"
  | "Click"
  | "Close"
  | "ComputerArrowUp"
  | "ConfirmationNumber"
  | "Copy"
  | "CreditCard"
  | "DarkMode"
  | "Delete"
  | "Divider"
  | "Dashboard"
  | "Download"
  | "Email"
  | "EditNote"
  | "Error"
  | "Exclamation"
  | "Filter"
  | "GoBack"
  | "Group"
  | "Happy"
  | "Handyman"
  | "Help"
  | "Home"
  | "Image"
  | "Info"
  | "KeyboardArrowDown"
  | "KeyboardArrowUp"
  | "KeyboardArrowRight"
  | "KeyboardArrowLeft"
  | "KeyboardDoubleArrowUp"
  | "LightMode"
  | "Lock"
  | "LockOpen"
  | "Logout"
  | "Menu"
  | "Modal"
  | "MoreHorizontal"
  | "MoreVertical"
  | "MoveToInbox"
  | "Numbers"
  | "OpenInNew"
  | "Pagination"
  | "PasswordVisibility"
  | "PasswordVisibilityOff"
  | "Pencil"
  | "Person"
  | "PlayArrow"
  | "Pause"
  | "ProgressActivity"
  | "ReceiptLong"
  | "ReleaseAlert"
  | "Refresh"
  | "Replay"
  | "Sad"
  | "SadSearch"
  | "Search"
  | "Settings"
  | "ShoppingCart"
  | "Skeleton"
  | "SortBy"
  | "Success"
  | "Sync"
  | "ShortArrowUp"
  | "ShortArrowDown"
  | "Table"
  | "Text"
  | "Trash"
  | "Upload"
  | "Users"
  | "Warning";

type Options = {
  [Key in IconName]: {
    name: string;
    type: "assets" | typeof MATERIAL_ICONS_CLASSNAME;
    extension?: "svg" | "png";
  };
};

export const Icon = ({
  name,
  size = "20px", // Pixel para evitar distorções
  className,
  id,
  onClick,
  "aria-label": ariaLabel,
  fill = false,
}: IconProps) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.(e);
    }
  };

  const icons: Options = {
    Add: { name: "add", type: MATERIAL_ICONS_CLASSNAME },
    AddPhoto: { name: "add_photo_alternate", type: MATERIAL_ICONS_CLASSNAME },
    AccountCircle: {
      name: "account_circle",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    Apps: { name: "apps", type: MATERIAL_ICONS_CLASSNAME },
    ArrowUp: {
      name: "north",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    ArrowLeft: {
      name: "arrow_left_alt",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    ArrowRight: {
      name: "arrow_right_alt",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    Article: { name: "article", type: MATERIAL_ICONS_CLASSNAME },
    Bell: { name: "bell", type: "assets", extension: "svg" },
    Calendar: { name: "calendar_month", type: MATERIAL_ICONS_CLASSNAME },
    Camera: { name: "camera_alt", type: MATERIAL_ICONS_CLASSNAME },
    Cancel: { name: "cancel", type: MATERIAL_ICONS_CLASSNAME },
    Carousel: { name: "view_carousel", type: MATERIAL_ICONS_CLASSNAME },
    Category: { name: "category", type: MATERIAL_ICONS_CLASSNAME },
    ChartBar: { name: "insert_chart", type: MATERIAL_ICONS_CLASSNAME },
    Chat: { name: "chat_bubble", type: MATERIAL_ICONS_CLASSNAME },
    Check: { name: "check", type: MATERIAL_ICONS_CLASSNAME },
    CheckCircle: { name: "check_circle", type: MATERIAL_ICONS_CLASSNAME },
    Checklist: { name: "checklist", type: MATERIAL_ICONS_CLASSNAME },
    ChevronLeft: { name: "chevron_left", type: MATERIAL_ICONS_CLASSNAME },
    ChevronRight: { name: "chevron_right", type: MATERIAL_ICONS_CLASSNAME },
    Click: { name: "ads_click", type: MATERIAL_ICONS_CLASSNAME },
    Close: { name: "close", type: MATERIAL_ICONS_CLASSNAME },
    ComputerArrowUp: { name: "present_to_all", type: MATERIAL_ICONS_CLASSNAME },
    ConfirmationNumber: { name: "confirmation_number", type: MATERIAL_ICONS_CLASSNAME },
    Copy: { name: "content_copy", type: MATERIAL_ICONS_CLASSNAME },
    CreditCard: { name: "credit_card", type: MATERIAL_ICONS_CLASSNAME },
    DarkMode: { name: "dark_mode", type: MATERIAL_ICONS_CLASSNAME },
    Delete: { name: "delete", type: MATERIAL_ICONS_CLASSNAME },
    Divider: { name: "divider", type: "assets", extension: "svg" },
    Dashboard: { name: "dashboard", type: MATERIAL_ICONS_CLASSNAME },
    Download: { name: "file_download", type: MATERIAL_ICONS_CLASSNAME },
    Email: { name: "mail", type: MATERIAL_ICONS_CLASSNAME },
    EditNote: { name: "edit_note", type: MATERIAL_ICONS_CLASSNAME },
    Error: { name: "error_outline", type: MATERIAL_ICONS_CLASSNAME },
    Exclamation: { name: "exclamation", type: MATERIAL_ICONS_CLASSNAME },
    Filter: { name: "filter_list", type: MATERIAL_ICONS_CLASSNAME },
    GoBack: { name: "arrow_back", type: MATERIAL_ICONS_CLASSNAME },
    Group: { name: "group", type: MATERIAL_ICONS_CLASSNAME },
    Happy: { name: "sentiment_satisfied", type: MATERIAL_ICONS_CLASSNAME },
    Handyman: { name: "handyman", type: MATERIAL_ICONS_CLASSNAME },
    Help: { name: "help", type: MATERIAL_ICONS_CLASSNAME },
    Home: { name: "home", type: MATERIAL_ICONS_CLASSNAME },
    Image: { name: "image", type: MATERIAL_ICONS_CLASSNAME },
    Info: { name: "info", type: MATERIAL_ICONS_CLASSNAME },
    KeyboardArrowDown: {
      name: "keyboard_arrow_down",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    KeyboardArrowUp: {
      name: "keyboard_arrow_up",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    KeyboardArrowRight: {
      name: "keyboard_arrow_right",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    KeyboardArrowLeft: {
      name: "keyboard_arrow_left",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    KeyboardDoubleArrowUp: {
      name: "keyboard_double_arrow_up",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    LightMode: { name: "light_mode", type: MATERIAL_ICONS_CLASSNAME },
    Lock: { name: "lock", type: MATERIAL_ICONS_CLASSNAME },
    LockOpen: { name: "lock_open", type: MATERIAL_ICONS_CLASSNAME },
    Logout: { name: "logout", type: MATERIAL_ICONS_CLASSNAME },
    Menu: { name: "menu", type: MATERIAL_ICONS_CLASSNAME },
    Modal: { name: "domain_verification", type: MATERIAL_ICONS_CLASSNAME },
    MoreHorizontal: {
      name: "more_horiz",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    MoreVertical: {
      name: "more_vert",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    MoveToInbox: {
      name: "move_to_inbox",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    Numbers: { name: "123", type: MATERIAL_ICONS_CLASSNAME },
    OpenInNew: { name: "open_in_new", type: MATERIAL_ICONS_CLASSNAME },
    Pagination: { name: "code", type: MATERIAL_ICONS_CLASSNAME },
    PasswordVisibility: {
      name: "visibility",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    PasswordVisibilityOff: {
      name: "visibility_off",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    Pencil: { name: "edit", type: MATERIAL_ICONS_CLASSNAME },
    Person: { name: "person", type: MATERIAL_ICONS_CLASSNAME },
    PlayArrow: { name: "play_arrow", type: MATERIAL_ICONS_CLASSNAME },
    Pause: { name: "pause", type: MATERIAL_ICONS_CLASSNAME },
    ProgressActivity: {
      name: "progress_activity",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    ReceiptLong: { name: "receipt_long", type: MATERIAL_ICONS_CLASSNAME },
    ReleaseAlert: { name: "release_alert", type: MATERIAL_ICONS_CLASSNAME },
    Refresh: { name: "refresh", type: MATERIAL_ICONS_CLASSNAME },
    Replay: { name: "replay", type: MATERIAL_ICONS_CLASSNAME },
    Sad: { name: "sentiment_dissatisfied", type: MATERIAL_ICONS_CLASSNAME },
    SadSearch: { name: "sad-search", type: "assets", extension: "svg" },
    Search: { name: "search", type: MATERIAL_ICONS_CLASSNAME },
    Settings: { name: "settings", type: MATERIAL_ICONS_CLASSNAME },
    ShoppingCart: { name: "shopping_cart", type: MATERIAL_ICONS_CLASSNAME },
    Skeleton: { name: "horizontal_split", type: MATERIAL_ICONS_CLASSNAME },
    SortBy: { name: "import_export", type: MATERIAL_ICONS_CLASSNAME },
    Success: { name: "check_circle", type: MATERIAL_ICONS_CLASSNAME },
    Sync: { name: "sync", type: MATERIAL_ICONS_CLASSNAME },
    ShortArrowUp: { name: "arrow_upward", type: MATERIAL_ICONS_CLASSNAME },
    ShortArrowDown: {
      name: "arrow_downward",
      type: MATERIAL_ICONS_CLASSNAME,
    },
    Table: { name: "table_rows", type: MATERIAL_ICONS_CLASSNAME },
    Text: { name: "text_snippet", type: MATERIAL_ICONS_CLASSNAME },
    Trash: { name: "delete_outline", type: MATERIAL_ICONS_CLASSNAME },
    Upload: { name: "upload", type: MATERIAL_ICONS_CLASSNAME },
    Users: { name: "people", type: MATERIAL_ICONS_CLASSNAME },
    Warning: { name: "warning_amber", type: MATERIAL_ICONS_CLASSNAME },
  };

  return (
    <>
      {icons[name].type === "assets" ? (
        <Image
          className={clsx("icon block align-middle", className)}
          style={{ width: size, height: size, minWidth: size, minHeight: size }}
          src={`/icons/${icons[name].name}.${icons[name].extension}`}
          alt={icons[name].name}
          id={id}
          width={24}
          height={24}
          onClick={onClick}
          priority
        />
      ) : (
        <span
          role="button"
          aria-label={ariaLabel}
          tabIndex={0}
          className={clsx("icon block cursor-[inherit] select-none align-middle", className, {
            "!cursor-pointer duration-300 hover:opacity-50": onClick,
            "!text-[length:var(--icon-size)]": size,
            [icons[name].type]: !fill,
            [MATERIAL_ICONS_FILLED_CLASSNAME]: fill,
          })}
          style={{ "--icon-size": size } as React.CSSProperties}
          id={id}
          onClick={onClick}
          onKeyDown={handleKeyDown}
        >
          {icons[name].name}
        </span>
      )}
    </>
  );
};
